import { AlertCircle, Edit2 } from 'lucide-react';

import { formatIcd10CodeForDisplay } from '@/shared/lib/formatters';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';

import type { LabOrderDraft } from '../../model/types.js';

interface RowProps {
  label: string;
  children: React.ReactNode;
}

function Row({ label, children }: RowProps) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

export interface ReviewStepProps {
  draft: LabOrderDraft;
  errorMessage: string | null;
  onEditDetails?: () => void;
}

export function ReviewStep({ draft, errorMessage, onEditDetails }: ReviewStepProps) {
  const isCustom = draft.mode === 'custom_test';

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-medium">Order Summary</span>
          {onEditDetails ? (
            <Button type="button" variant="ghost" size="sm" onClick={onEditDetails}>
              <Edit2 className="mr-1 size-3" /> Edit
            </Button>
          ) : null}
        </div>

        <dl className="space-y-3">
          <Row label={isCustom ? 'Custom Test' : 'Lab Test'}>
            {isCustom ? draft.name : (draft.labTestName ?? draft.labTestId)}
          </Row>

          {isCustom && (
            <>
              <Row label="Description">{draft.description}</Row>
              <Row label="Method">{draft.method.replace(/_/g, ' ')}</Row>
              <Row label="Biomarkers">
                <span className="flex flex-wrap gap-1">
                  {draft.selectedMarkers.map((m) => (
                    <span key={m.id} className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      {m.name}
                    </span>
                  ))}
                </span>
              </Row>
            </>
          )}

          {!isCustom && draft.selectedLabTest && (
            <Row label="AOE Answered">{Object.keys(draft.aoeAnswersById).length} question(s)</Row>
          )}

          {draft.icdCodes.length > 0 && (
            <Row label="ICD-10 Codes">
              <span className="flex flex-wrap gap-1">
                {draft.icdCodes.map((c) => (
                  <span key={c.code} className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    <span className="font-semibold">{formatIcd10CodeForDisplay(c.code)}</span>{' '}
                    <span className="opacity-80">{c.description}</span>
                  </span>
                ))}
              </span>
            </Row>
          )}

          {draft.priority && (
            <Row label="Priority">
              <span className="flex items-center gap-1 font-medium text-amber-600">
                <AlertCircle className="size-4" /> STAT
              </span>
            </Row>
          )}

          {draft.notes.trim() && <Row label="Notes">{draft.notes}</Row>}
        </dl>
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Submission issue</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
